'use client'

import Image from "next/image";
import {useCallback, useEffect, useRef, useState} from "react";
import type {ReactNode} from "react";
import {Activity, CheckCircle2, Radio, ShieldCheck} from "lucide-react";
import DevPlanerLogo from "@/assets/logo.png";
import {
    getMaintenanceState,
    MAINTENANCE_EVENT,
    maintenanceStreamUrl,
    normalizeMaintenanceState,
} from "@/lib/platform";
import type {MaintenanceState} from "@/lib/platform";

interface MaintenanceGateProps {
    children: ReactNode;
    initialState: MaintenanceState;
}

type ConnectionState = "connecting" | "live" | "fallback";

const FALLBACK_INTERVAL_MS = 2_000;

export function MaintenanceGate({children, initialState}: MaintenanceGateProps) {
    const [maintenance, setMaintenance] = useState(initialState);
    const [connection, setConnection] = useState<ConnectionState>("connecting");
    const currentState = useRef(initialState);

    const applyState = useCallback((next: MaintenanceState) => {
        const wasEnabled = currentState.current.enabled;
        currentState.current = next;
        setMaintenance(next);

        if (wasEnabled && !next.enabled) {
            window.location.reload();
        }
    }, []);

    const checkStatus = useCallback(async () => {
        const next = await getMaintenanceState();
        if (next) {
            applyState(next);
        }
    }, [applyState]);

    useEffect(() => {
        let eventSource: EventSource | null = null;
        let fallbackTimer: number | null = null;

        const stopFallback = () => {
            if (fallbackTimer !== null) {
                window.clearInterval(fallbackTimer);
                fallbackTimer = null;
            }
        };

        const startFallback = () => {
            if (fallbackTimer !== null) {
                return;
            }
            setConnection("fallback");
            fallbackTimer = window.setInterval(() => {
                if (document.visibilityState === "visible") {
                    void checkStatus();
                }
            }, FALLBACK_INTERVAL_MS);
        };

        const onMaintenanceEvent = (event: Event) => {
            const detail = (event as CustomEvent<unknown>).detail;
            applyState(normalizeMaintenanceState(detail));
        };

        const onStreamMessage = (event: Event) => {
            try {
                const message = event as MessageEvent<string>;
                applyState(normalizeMaintenanceState(JSON.parse(message.data) as unknown));
            } catch {
                void checkStatus();
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void checkStatus();
            }
        };

        window.addEventListener(MAINTENANCE_EVENT, onMaintenanceEvent);
        document.addEventListener("visibilitychange", onVisibilityChange);

        if (typeof EventSource !== "undefined") {
            eventSource = new EventSource(maintenanceStreamUrl());
            eventSource.addEventListener("maintenance", onStreamMessage);
            eventSource.onopen = () => {
                stopFallback();
                setConnection("live");
            };
            eventSource.onerror = startFallback;
        } else {
            startFallback();
        }

        void checkStatus();

        return () => {
            stopFallback();
            eventSource?.close();
            window.removeEventListener(MAINTENANCE_EVENT, onMaintenanceEvent);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [applyState, checkStatus]);

    if (!maintenance.enabled) {
        return children;
    }

    const connectionLabel = connection === "live"
        ? "Live verbunden"
        : connection === "fallback"
            ? "Verbindung wird wiederhergestellt"
            : "Verbindung wird aufgebaut";

    return (
        <main className="dpm-page">
            <div className="dpm-background-grid" aria-hidden="true" />
            <div className="dpm-glow dpm-glow-left" aria-hidden="true" />
            <div className="dpm-glow dpm-glow-right" aria-hidden="true" />

            <div className="dpm-shell">
                <header className="dpm-header">
                    <div className="dpm-brand">
                        <Image
                            src={DevPlanerLogo}
                            alt="DevPlaner"
                            className="dpm-brand-logo"
                            priority
                        />
                        <div>
                            <div className="dpm-brand-name">DevPlaner</div>
                            <div className="dpm-brand-subtitle">Systemstatus</div>
                        </div>
                    </div>

                    <div className="dpm-connection">
                        <span className={`dpm-connection-dot ${connection === "live" ? "is-live" : "is-connecting"}`} />
                        <span>{connectionLabel}</span>
                    </div>
                </header>

                <section className="dpm-hero">
                    <div className="dpm-copy">
                        <div className="dpm-eyebrow">
                            <span />
                            Wartungsarbeiten
                        </div>

                        <h1 className="dpm-title">
                            Wir sind gleich<br />
                            wieder für dich da.
                        </h1>

                        <p className="dpm-message">{maintenance.message}</p>

                        <div className="dpm-live-note">
                            <div className="dpm-live-note-icon">
                                <Activity aria-hidden="true" />
                            </div>
                            <div>
                                <strong>Du musst die Seite nicht neu laden.</strong>
                                <p>DevPlaner ist live verbunden und öffnet sich nach der Freigabe automatisch.</p>
                            </div>
                        </div>
                    </div>

                    <div className="dpm-visual-column" aria-label="Wartungsstatus">
                        <div className="dpm-visual">
                            <div className="dpm-ring dpm-ring-outer" />
                            <div className="dpm-ring dpm-ring-rotating" />
                            <div className="dpm-ring dpm-ring-inner" />
                            <span className="dpm-orbit-dot dpm-orbit-dot-blue" />
                            <span className="dpm-orbit-dot dpm-orbit-dot-green" />

                            <div className="dpm-core">
                                <div className="dpm-core-glow" />
                                <Image src={DevPlanerLogo} alt="" className="dpm-core-logo" />
                                <span>Wartung aktiv</span>
                            </div>
                        </div>

                        <div className="dpm-work-status">
                            <div className="dpm-work-status-row">
                                <span>Systemarbeiten laufen</span>
                                <strong>LIVE</strong>
                            </div>
                            <div className="dpm-progress"><span /></div>
                        </div>
                    </div>
                </section>

                <footer className="dpm-footer">
                    <div className="dpm-footer-item">
                        <Radio aria-hidden="true" />
                        <div>
                            <strong>Live-Synchronisierung</strong>
                            <span>Reagiert sofort auf die Admin-App</span>
                        </div>
                    </div>
                    <div className="dpm-footer-item">
                        <ShieldCheck aria-hidden="true" />
                        <div>
                            <strong>Daten bleiben sicher</strong>
                            <span>Workspaces werden nicht verändert</span>
                        </div>
                    </div>
                    <div className="dpm-footer-item">
                        <CheckCircle2 aria-hidden="true" />
                        <div>
                            <strong>Automatische Freigabe</strong>
                            <span>Kein manuelles Neuladen nötig</span>
                        </div>
                    </div>
                </footer>
            </div>

            <style jsx global>{`
                .dpm-page,
                .dpm-page * {
                    box-sizing: border-box;
                }

                .dpm-page {
                    position: relative;
                    isolation: isolate;
                    min-height: 100vh;
                    min-height: 100svh;
                    overflow: hidden;
                    background: #080d16;
                    color: #f8fafc;
                    font-family: var(--font-geist-sans), Inter, ui-sans-serif, system-ui, sans-serif;
                }

                .dpm-background-grid {
                    position: absolute;
                    inset: 0;
                    z-index: -3;
                    opacity: 0.035;
                    background-image:
                        linear-gradient(rgba(255, 255, 255, 0.7) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255, 255, 255, 0.7) 1px, transparent 1px);
                    background-size: 72px 72px;
                    mask-image: linear-gradient(to bottom, black, transparent 92%);
                }

                .dpm-glow {
                    position: absolute;
                    z-index: -2;
                    border-radius: 999px;
                    filter: blur(110px);
                    pointer-events: none;
                }

                .dpm-glow-left {
                    left: -180px;
                    top: 18%;
                    width: 460px;
                    height: 460px;
                    background: rgba(19, 105, 200, 0.2);
                }

                .dpm-glow-right {
                    right: -140px;
                    bottom: -180px;
                    width: 420px;
                    height: 420px;
                    background: rgba(20, 94, 181, 0.12);
                }

                .dpm-shell {
                    width: min(1280px, calc(100% - 80px));
                    min-height: 100vh;
                    min-height: 100svh;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                }

                .dpm-header {
                    min-height: 96px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 24px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }

                .dpm-brand {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                }

                .dpm-brand-logo {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    object-fit: cover;
                }

                .dpm-brand-name {
                    font-size: 15px;
                    line-height: 1.2;
                    font-weight: 650;
                    letter-spacing: -0.02em;
                }

                .dpm-brand-subtitle {
                    margin-top: 5px;
                    color: #64748b;
                    font-size: 12px;
                }

                .dpm-connection {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #cbd5e1;
                    font-size: 12px;
                    font-weight: 550;
                }

                .dpm-connection-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 999px;
                }

                .dpm-connection-dot.is-live {
                    background: #34d399;
                    box-shadow: 0 0 14px rgba(52, 211, 153, 0.75);
                }

                .dpm-connection-dot.is-connecting {
                    background: #fbbf24;
                    box-shadow: 0 0 14px rgba(251, 191, 36, 0.5);
                    animation: dpm-pulse 1.2s ease-in-out infinite;
                }

                .dpm-hero {
                    flex: 1;
                    min-height: 590px;
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) 420px;
                    align-items: center;
                    gap: 96px;
                    padding: 54px 0;
                }

                .dpm-copy {
                    max-width: 760px;
                }

                .dpm-eyebrow {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    margin-bottom: 25px;
                    color: #7dbbff;
                    font-size: 12px;
                    line-height: 1;
                    font-weight: 700;
                    letter-spacing: 0.22em;
                    text-transform: uppercase;
                }

                .dpm-eyebrow span {
                    display: block;
                    width: 36px;
                    height: 1px;
                    background: #4da3ff;
                }

                .dpm-title {
                    max-width: 760px;
                    margin: 0;
                    color: #ffffff;
                    font-size: clamp(52px, 5vw, 78px);
                    line-height: 0.99;
                    font-weight: 650;
                    letter-spacing: -0.055em;
                }

                .dpm-message {
                    max-width: 620px;
                    margin: 31px 0 0;
                    color: #aebdd0;
                    font-size: 18px;
                    line-height: 1.7;
                }

                .dpm-live-note {
                    max-width: 590px;
                    margin-top: 39px;
                    padding: 19px 21px;
                    display: flex;
                    align-items: flex-start;
                    gap: 15px;
                    border: 1px solid rgba(96, 165, 250, 0.14);
                    border-radius: 16px;
                    background: rgba(17, 31, 51, 0.58);
                    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.14);
                    backdrop-filter: blur(16px);
                }

                .dpm-live-note-icon {
                    width: 36px;
                    height: 36px;
                    flex: 0 0 36px;
                    display: grid;
                    place-items: center;
                    border-radius: 11px;
                    background: rgba(37, 99, 235, 0.15);
                    color: #7dbbff;
                }

                .dpm-live-note-icon svg {
                    width: 18px;
                    height: 18px;
                }

                .dpm-live-note strong {
                    display: block;
                    color: #f1f5f9;
                    font-size: 14px;
                    line-height: 1.4;
                    font-weight: 620;
                }

                .dpm-live-note p {
                    margin: 5px 0 0;
                    color: #8292a8;
                    font-size: 13px;
                    line-height: 1.55;
                }

                .dpm-visual-column {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 26px;
                }

                .dpm-visual {
                    position: relative;
                    width: 360px;
                    height: 360px;
                }

                .dpm-ring {
                    position: absolute;
                    border-radius: 50%;
                }

                .dpm-ring-outer {
                    inset: 0;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                }

                .dpm-ring-rotating {
                    inset: 31px;
                    border: 1px dashed rgba(77, 163, 255, 0.38);
                    animation: dpm-spin 16s linear infinite;
                }

                .dpm-ring-inner {
                    inset: 72px;
                    border: 1px solid rgba(77, 163, 255, 0.18);
                    background: radial-gradient(circle, rgba(35, 113, 210, 0.09), transparent 70%);
                }

                .dpm-core {
                    position: absolute;
                    inset: 98px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                    background: #0c1625;
                    box-shadow: 0 26px 80px rgba(0, 0, 0, 0.35);
                }

                .dpm-core-glow {
                    position: absolute;
                    inset: -40px;
                    background: radial-gradient(circle, rgba(38, 125, 226, 0.19), transparent 62%);
                }

                .dpm-core-logo {
                    position: relative;
                    width: 68px;
                    height: 68px;
                    border-radius: 18px;
                    object-fit: cover;
                    filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.25));
                }

                .dpm-core span {
                    position: relative;
                    margin-top: 15px;
                    color: #83bdff;
                    font-size: 10px;
                    font-weight: 750;
                    letter-spacing: 0.18em;
                    text-transform: uppercase;
                }

                .dpm-orbit-dot {
                    position: absolute;
                    display: block;
                    border: 3px solid #080d16;
                    border-radius: 50%;
                }

                .dpm-orbit-dot-blue {
                    top: 74px;
                    left: 37px;
                    width: 14px;
                    height: 14px;
                    background: #60a5fa;
                    box-shadow: 0 0 20px rgba(96, 165, 250, 0.9);
                }

                .dpm-orbit-dot-green {
                    right: 49px;
                    bottom: 51px;
                    width: 12px;
                    height: 12px;
                    background: #34d399;
                    box-shadow: 0 0 18px rgba(52, 211, 153, 0.8);
                }

                .dpm-work-status {
                    width: 330px;
                    padding: 16px 17px;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 13px;
                    background: rgba(12, 22, 37, 0.72);
                }

                .dpm-work-status-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    color: #cbd5e1;
                    font-size: 12px;
                }

                .dpm-work-status-row strong {
                    color: #5ee0ad;
                    font-size: 9px;
                    letter-spacing: 0.18em;
                }

                .dpm-progress {
                    height: 3px;
                    margin-top: 13px;
                    overflow: hidden;
                    border-radius: 999px;
                    background: rgba(255, 255, 255, 0.07);
                }

                .dpm-progress span {
                    display: block;
                    width: 45%;
                    height: 100%;
                    border-radius: inherit;
                    background: linear-gradient(90deg, #277fd9, #69b2ff);
                    box-shadow: 0 0 14px rgba(96, 165, 250, 0.65);
                    animation: dpm-progress 2.4s ease-in-out infinite;
                }

                .dpm-footer {
                    min-height: 100px;
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    border-top: 1px solid rgba(255, 255, 255, 0.08);
                }

                .dpm-footer-item {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    min-width: 0;
                    padding: 22px 28px;
                }

                .dpm-footer-item:first-child {
                    padding-left: 0;
                }

                .dpm-footer-item + .dpm-footer-item {
                    border-left: 1px solid rgba(255, 255, 255, 0.08);
                }

                .dpm-footer-item svg {
                    width: 18px;
                    height: 18px;
                    flex: 0 0 18px;
                    color: #6eb3ff;
                }

                .dpm-footer-item strong,
                .dpm-footer-item span {
                    display: block;
                }

                .dpm-footer-item strong {
                    color: #dce5f0;
                    font-size: 13px;
                    line-height: 1.3;
                    font-weight: 620;
                }

                .dpm-footer-item span {
                    margin-top: 4px;
                    overflow: hidden;
                    color: #64748b;
                    font-size: 11px;
                    line-height: 1.4;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                @keyframes dpm-spin {
                    to { transform: rotate(360deg); }
                }

                @keyframes dpm-pulse {
                    0%, 100% { opacity: 0.55; transform: scale(0.9); }
                    50% { opacity: 1; transform: scale(1); }
                }

                @keyframes dpm-progress {
                    0% { transform: translateX(-115%); }
                    55%, 100% { transform: translateX(240%); }
                }

                @media (max-width: 980px) {
                    .dpm-shell {
                        width: min(760px, calc(100% - 48px));
                    }

                    .dpm-hero {
                        grid-template-columns: 1fr;
                        gap: 48px;
                        padding: 64px 0;
                    }

                    .dpm-copy {
                        max-width: 700px;
                    }

                    .dpm-visual-column {
                        display: none;
                    }

                    .dpm-footer {
                        grid-template-columns: 1fr;
                        padding: 12px 0;
                    }

                    .dpm-footer-item,
                    .dpm-footer-item:first-child {
                        padding: 13px 0;
                    }

                    .dpm-footer-item + .dpm-footer-item {
                        border-top: 1px solid rgba(255, 255, 255, 0.07);
                        border-left: 0;
                    }
                }

                @media (max-width: 620px) {
                    .dpm-page {
                        overflow-y: auto;
                    }

                    .dpm-shell {
                        width: calc(100% - 36px);
                    }

                    .dpm-header {
                        min-height: 82px;
                    }

                    .dpm-brand-logo {
                        width: 38px;
                        height: 38px;
                        border-radius: 10px;
                    }

                    .dpm-connection span:last-child {
                        max-width: 95px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .dpm-hero {
                        min-height: 0;
                        padding: 58px 0 66px;
                    }

                    .dpm-eyebrow {
                        margin-bottom: 20px;
                        font-size: 10px;
                    }

                    .dpm-title {
                        font-size: clamp(39px, 12vw, 54px);
                        line-height: 1.02;
                    }

                    .dpm-message {
                        margin-top: 24px;
                        font-size: 16px;
                    }

                    .dpm-live-note {
                        margin-top: 31px;
                        padding: 17px;
                    }

                    .dpm-footer {
                        min-height: 0;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .dpm-ring-rotating,
                    .dpm-progress span,
                    .dpm-connection-dot {
                        animation: none !important;
                    }
                }
            `}</style>
        </main>
    );
}
